import { EntityLevel } from '@/composables/useEntityLevel'
import {
  compileDashboardFeature,
  ComponentFilters,
  DashboardComponent,
  DashboardComponentType,
  Measurement,
  MeasurementFilters
} from '../dashboard'
import { FormatOptions } from '@/components/AnalyticsDashboard/formatter'
import { LocaleString } from '@/components/types'
import { DeepPartialNull } from '@/utils/types'

const enabledEntityLevelsColoredEntities: EntityLevel[] = [
  'COMPLEX',
  'REAL_ESTATE',
  'CONSTRUCTION',
  'FLOOR',
  'LEVEL',
  'SPACE'
]

const format: FormatOptions = {
  format: {
    type: 'intl-number',
    maximumFractionDigits: 1
  },
  steps: [{ format: { type: 'format-string', format: '%s kWh' } }]
}

const colors = ['#40A9FF', '#9254DE', '#36CFC9', '#FFC53D', '#F759AB', '#36CFC9', '#FF7A45']

type FastMeasurement = {
  type: 'avg' | 'sum'
  title: LocaleString
  filters?: ComponentFilters
}

const createFilter = (type: string, source?: string): ComponentFilters => {
  const filter: DeepPartialNull<MeasurementFilters> = {
    query: {
      query: {
        filters: {
          type: 'and',
          conditions: [
            {
              type: 'eq',
              key: 'energy_type',
              value: type
            },
            ...(source ? [{ type: 'eq' as const, key: 'energy_source', value: source }] : [])
          ]
        }
      }
    }
  }
  return {
    value: filter,
    values: filter
  }
}

const measurementName = 'energy'
const fieldName = 'energy'

const measurements: FastMeasurement[] = [
  {
    type: 'sum',
    title: {
      sv: 'Inköpt energi',
      en: 'Bought energy'
    },
    filters: createFilter('bought')
  },
  {
    type: 'sum',
    title: {
      sv: 'Producerad energi',
      en: 'Produced energy'
    },
    filters: createFilter('produced')
  },
  {
    type: 'sum',
    title: {
      sv: 'Såld energi',
      en: 'Sold energy'
    },
    filters: createFilter('sold')
  },
  {
    type: 'sum',
    title: {
      sv: 'Förbrukad energi',
      en: 'Consumed energy'
    },
    filters: createFilter('consumed')
  }
]

const subBarFormats = {
  district_cooling: {
    color: '#EFDBFF',
    label: {
      en: 'District cooling',
      sv: 'Fjärrkyla'
    }
  },
  district_heating: {
    color: '#9254DE',
    label: {
      en: 'District heating',
      sv: 'Fjärrvärme'
    }
  },
  electricity: {
    color: '#391085',
    label: {
      en: 'Electricity',
      sv: 'El'
    }
  },
  solar_electricity: {
    color: '#10072B',
    label: {
      en: 'Solar electricity',
      sv: 'Solenergi'
    }
  }
}

export const actualEnergy = compileDashboardFeature({
  name: 'energy-analysis-actual',
  title: {
    sv: 'Verklig Energi',
    en: 'Actual Energy'
  },
  dataSectionTitle: {
    sv: 'Energi',
    en: 'Energy'
  },
  icon: 'IconZap',
  enabled: false,
  defaultFilters: {
    calendar: {
      quickSelect: 'last30days'
    },
    defaultDateSelection: 'last24h',
    onlyBusinessDays: false,
    onlyBusinessHours: false,
    dateSelections: [
      'now',
      'last24h',
      'yesterday',
      'last7d',
      'last30d',
      'lastquarter',
      'last12m',
      'currentyear',
      'lastyear',
      'custom'
    ]
  },
  measurements: measurements.map(
    (measurement, i): Measurement => ({
      name: measurementName,
      field: {
        name: fieldName + '_' + measurement.type + '_' + i,
        expression: {
          type: measurement.type,
          exp: {
            type: 'field',
            name: fieldName
          }
        }
      },
      filters: measurement.filters,
      title: measurement.title,
      options: {
        color: colors[i % colors.length],
        enabledEntityLevels: enabledEntityLevelsColoredEntities,
        format: format,
        legend: { type: 'slider' }
      }
    })
  ),
  components: [
    ...measurements.flatMap((measurement, index): DashboardComponent[] => [
      {
        type: DashboardComponentType.Kpi,
        name: `energy-${measurement.type}-kpi-${index}`,
        measurement: `${measurementName}.${fieldName}_${measurement.type}_${index}`,
        details: {
          groups: ['energy_source']
        },
        info: {
          sv: '',
          en: ''
        }
      }
    ]),
    {
      type: DashboardComponentType.TimeSeries,
      name: 'energy-time-series',
      measurements: measurements.map(
        (measurement, i) => `${measurementName}.${fieldName}_${measurement.type}_${i}`
      ),
      title: {
        sv: 'Energi över tid',
        en: 'Energy over time'
      },
      info: {
        sv: '',
        en: ''
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'energy-bar-chart',
      measurements: [measurements[0]].map(
        (measurement, i) => `${measurementName}.${fieldName}_${measurement.type}_${i}`
      ),
      title: {
        sv: 'Förändring inköpt energi över tid'
      },
      groupKey: 'timestamp',
      subGroupKey: 'energy_source',
      subBarFormats,
      filters: {
        values: {
          query: {
            groupBy: [
              { type: 'timestamp_bucket', interval: '1 week' },
              { type: 'tag', tag: 'energy_source' }
            ]
          }
        }
      },
      info: {
        sv: '',
        en: ''
      }
    },
    ...measurements.map(
      (measurement, index): DashboardComponent => ({
        type: DashboardComponentType.Toplist,
        name: 'energy-' + fieldName + '-toplist-' + measurement.type + '-' + index,
        measurements: [`${measurementName}.${fieldName}_${measurement.type}_${index}`],
        title: {
          sv: `${measurement.title.sv} per entitet`,
          en: `${measurement.title.en} per entity`
        },
        filters: {
          values: {
            query: {
              groupBy: [{ type: 'path_subtree' }],
              query: {
                orderBy: [
                  {
                    field: `${fieldName}_${measurements[0].type}_0`,
                    direction: 'DESC',
                    nulls: 'LAST'
                  }
                ]
              }
            }
          }
        },
        info: {
          sv: '',
          en: ''
        }
      })
    )
  ]
})
